import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import { tokens } from '../../../theme';
import { useEnableReleaseProductMutation } from '../../../features/organizations/organizationsApiSlice';

const normalizeFormats = (formats) => {
    if (!Array.isArray(formats)) return [];

    return [...new Set(
        formats
            .map((fmt) => String(fmt || '').trim().toLowerCase())
            .filter(Boolean)
    )].sort();
};

const normalizeS3Uri = (value) => {
    if (!value) return '';

    const cleaned = String(value)
        .trim()
        .replace(/^s3:\/\//i, '')
        .replace(/^\/+|\/+$/g, '');

    return cleaned ? `s3://${cleaned}` : '';
};

const buildProductFolder = (name) => {
    return String(name || 'product')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const getOrgDeliverySummary = (organization) => {
    const s3Config = organization?.s3_config || organization?.s3Config || {};
    const onpremConfig = s3Config?.infra_data?.onprem_config;
    const snowflakeConfig = s3Config?.infra_data?.snowflake_config;

    const bucketCreated = !!onpremConfig?.bucket_created;
    const onpremBucket = onpremConfig?.onprem_bucket;
    const s3Uri = onpremConfig?.s3_uri;

    const s3Base = onpremBucket
        ? `s3://${String(onpremBucket).replace(/^s3:\/\//i, '').replace(/\/+$/g, '')}`
        : normalizeS3Uri(s3Uri);

    const s3Ready = Boolean(onpremConfig && (bucketCreated || onpremBucket || s3Uri));

    const snowflakeReady = Boolean(
        snowflakeConfig?.database &&
        snowflakeConfig?.schema
    );

    const preferredFormats = normalizeFormats(
        organization?.delivery_format ||
        organization?.deliveryFormat ||
        organization?.delivery_formats ||
        []
    );

    return {
        s3Ready,
        s3Base,
        snowflakeReady,
        snowflakeConfig,
        preferredFormats,
    };
};

const getFormatLabel = (fmt) => {
    if (fmt === 'csv') return 'S3 CSV';
    if (fmt === 'parquet') return 'S3 Parquet';
    if (fmt === 'snowflake') return 'Snowflake Copy';
    return fmt;
};

const EnableReleaseProductDialog = ({
    open,
    onClose,
    onConfirm,
    asset,
    organization,
    isSubmitting = false,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const delivery = useMemo(
        () => getOrgDeliverySummary(organization),
        [organization]
    );

    const productFolder = useMemo(
        () => buildProductFolder(asset?.name),
        [asset?.name]
    );

    const effectiveFormats = useMemo(() => {
        const formats = delivery.preferredFormats;

        if (formats.length) return formats;

        const inferred = [];

        if (delivery.s3Ready) {
            inferred.push('csv');
            inferred.push('parquet');
        }

        if (delivery.snowflakeReady) {
            inferred.push('snowflake');
        }

        return inferred;
    }, [delivery]);

    const hasAnyDeliveryTarget = effectiveFormats.length > 0;

    const renderDestination = (fmt) => {
        if (fmt === 'csv' || fmt === 'parquet') {
            if (!delivery.s3Ready) {
                return 'S3 is not configured for this organization.';
            }

            return `${delivery.s3Base}/outgoing/${productFolder}/`;
        }

        if (fmt === 'snowflake') {
            if (!delivery.snowflakeReady) {
                return 'Snowflake is not configured for this organization.';
            }

            const db = delivery.snowflakeConfig?.database;
            const schema = delivery.snowflakeConfig?.schema;
            const table = delivery.snowflakeConfig?.table;

            return table ? `${db}.${schema}.${table}` : `${db}.${schema}`;
        }

        return 'Unknown delivery target.';
    };

    const [enableReleaseProduct, { isLoading: enabling, error: enableError }] =
        useEnableReleaseProductMutation();

    const submitting = isSubmitting || enabling;

    const handleConfirm = async () => {
    if (!asset || submitting) return;

    const id = organization?.organization_id;
    const product = asset?.name;

    if (!id || !product) return;

    await enableReleaseProduct({
        id,
        product,
    }).unwrap();

    onConfirm?.(asset);
};



    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: `linear-gradient(135deg, ${colors.primary[400]}, ${colors.primary[900]})`,
                    borderRadius: '18px',
                    border: `1px solid ${colors.primary[300]}`,
                },
            }}
        >
            <DialogTitle>
                Enable Release Product
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="h4" color="textPrimary">
                            {asset?.name || 'Release Product'}
                        </Typography>

                        {enableError ? (
                            <Alert severity="error" variant="filled">
                                {enableError?.data?.message || enableError?.error || 'Failed to enable release product.'}
                            </Alert>
                        ) : null}

                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                            <Chip
                                label={asset?.release || 'Release Product'}
                                sx={{
                                    bgcolor: colors.blueAccent[700],
                                    color: colors.grey[100],
                                }}
                            />

                            <Chip
                                label="Release Delivery"
                                sx={{
                                    bgcolor: colors.greenAccent[700],
                                    color: colors.grey[100],
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ borderColor: colors.primary[300] }} />

                    <Box>
                        <Typography variant="h5" color="textPrimary" mb={1}>
                            Delivery Preview
                        </Typography>

                        <Typography variant="body2" color="textSecondary">
                            Enabling this product adds it to the organization’s available release
                            products. Future release planning will use this organization’s configured
                            delivery destinations.
                        </Typography>
                    </Box>

                    {!hasAnyDeliveryTarget ? (
                        <Alert severity="warning" variant="filled">
                            This organization does not have S3 or Snowflake delivery configured yet.
                            You can enable the product, but release delivery planning may skip this
                            organization until a delivery target is configured.
                        </Alert>
                    ) : (
                        <Stack spacing={1.5}>
                            {effectiveFormats.map((fmt) => {
                                const isS3 = fmt === 'csv' || fmt === 'parquet';
                                const isSnowflake = fmt === 'snowflake';
                                const routeReady = isS3
                                    ? delivery.s3Ready
                                    : isSnowflake
                                        ? delivery.snowflakeReady
                                        : false;

                                return (
                                    <Paper
                                        key={fmt}
                                        elevation={1}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '12px',
                                            background: colors.primary[500],
                                            border: `1px solid ${routeReady
                                                ? colors.greenAccent[600]
                                                : colors.redAccent[600]
                                                }`,
                                        }}
                                    >
                                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                            <Chip
                                                size="small"
                                                label={getFormatLabel(fmt)}
                                                sx={{
                                                    bgcolor: routeReady
                                                        ? colors.greenAccent[700]
                                                        : colors.redAccent[700],
                                                    color: colors.grey[100],
                                                }}
                                            />

                                            <Typography variant="body2" color="textSecondary">
                                                {routeReady ? 'Ready' : 'Not configured'}
                                            </Typography>
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            color="textPrimary"
                                            mt={1}
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            {renderDestination(fmt)}
                                        </Typography>
                                    </Paper>
                                );
                            })}
                        </Stack>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="contained"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{
                        backgroundColor: '#ffffff',
                        color: colors.redAccent[500],
                        border: `2px solid ${colors.redAccent[500]}`,
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: colors.redAccent[900],
                            color: '#ffffff',
                            borderColor: colors.redAccent[400],
                        },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={!asset || submitting}
                    sx={{
                        backgroundColor: colors.greenAccent[600],
                        color: colors.grey[100],
                        fontWeight: 700,
                        '&:hover': {
                            backgroundColor: colors.greenAccent[500],
                        },
                    }}
                >
                    {submitting ? 'Enabling...' : 'Confirm Enable'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EnableReleaseProductDialog;